import type React from 'react';
import { useState } from 'react';
import styled from 'styled-components';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Cookies from 'js-cookie';
import { isMobile } from 'react-device-detect';
import type { IAboutInfo } from '@/engine/app/modules/ui/UIConfigSchema';
import { useSurgerySpecificUiConfigValue } from '@/engine/app/modules/ui';

const MOBILE_MIN_WIDTH = 800;
const DESKTOP_MIN_WIDTH = 1280;
const DESKTOP_MIN_HEIGHT = 600;
const COOKIE_NAME = 'ScreenDialog';

function shouldShowScreenNotification(): boolean {
  const isAlreadyShown = Cookies.get(COOKIE_NAME) === 'Opened';

  const isDesktopResolutionNotFit =
    !isMobile &&
    (document.documentElement.clientHeight < DESKTOP_MIN_HEIGHT ||
      document.documentElement.clientWidth < DESKTOP_MIN_WIDTH);

  const landscapeWidth = window.screen.width > window.screen.height ? window.screen.width : window.screen.height;

  const isMobileResolutionNotFit = isMobile && landscapeWidth < MOBILE_MIN_WIDTH;
  return (isDesktopResolutionNotFit || isMobileResolutionNotFit) && !isAlreadyShown;
}

export const ScreenNotificationModal: React.FC = () => {
  const [open, setOpen] = useState(shouldShowScreenNotification());

  const aboutInfo = useSurgerySpecificUiConfigValue<IAboutInfo>('aboutInfo');

  const handleClose = (): void => {
    setOpen(false);
    Cookies.set(COOKIE_NAME, 'Opened', { path: '/', expires: 365 });
  };

  return (
    <div>
      <Dialog open={open}>
        <ModalContainer>
          <DialogTitle id="screen-notification-dialog-title" sx={{ m: 0, p: 2, fontWeight: '600' }}>
            Note
          </DialogTitle>
          <DialogContent dividers>
            <ModalContentBody>
              The screen size, window size or resolution of your current device does not allow to properly visualize the
              application and usability of the application could be affected. Please maximize the window (if not the
              case) or consider using another device for an optimal experience. Please refer to the{' '}
              <a href={aboutInfo.manualUrl} target="_blank" rel="noreferrer">
                Manual
              </a>{' '}
              for minimal requirements to use the application.
            </ModalContentBody>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'end', alignItems: 'center' }}>
            <Button onClick={handleClose}>Continue</Button>
          </DialogActions>
        </ModalContainer>
      </Dialog>
    </div>
  );
};

const ModalContainer = styled.div`
  width: 590px;
  padding-bottom: 10px;
`;

const ModalContentBody = styled.div`
  p.current-year {
    text-align: right;
    font-size: 0.8em;
  }
`;
